import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withWizard } from 'react-albus'
import { injectIntl } from 'react-intl'
import _ from 'lodash'
import { parsePhoneNumber } from 'react-phone-number-input/max'
import { notification } from 'antd'
import moment from 'moment'
import { replaceColor } from 'lottie-colorify'

import RequesterConfiguration, {
  requesterConfigurationMethods
} from 'core/requesters/RequesterConfiguration'
import RequesterGlobal, { requesterGlobalMethods } from 'core/requesters/RequesterGlobal'
import RequesterTradeIn, { RequesterTradeInMethods } from 'core/requesters/RequesterTradeIn'
import Hydrator, { hydratorMethods, hydratorOrigins } from 'core/hydrator'

import * as animation from 'assets/animations/trade-in-context.json'

import * as wizardStep from 'core/routing/constants/index'
import { getDistanceFromCoordinates } from 'core/utils/geolocation'
import { activateStep } from 'core/utils/wizard'
import getEquipmentGroupings from 'core/utils/equipments'

import { setTradeIn } from './actions'
import { setConfiguration } from '../configuration/actions'

import TradeInContextComponent from './TradeInContext'

const TradeInContext = ({ intl, actions, ...props }) => {
  const [animationOptions, setAnimationOptions] = useState()
  const [loading, setLoading] = useState(true)

  const { tradeInContext, campaign, theme } = props

  useEffect(() => {
    if (tradeInContext.car) {
      setAnimationOptions({
        loop: true,
        autoplay: true,
        animationData: replaceColor(
          [30, 171, 255],
          theme.colors.color1.bg,
          replaceColor([32, 62, 128], theme.colors.color2.bg, animation.default)
        ),
        speed: 2
      })
      actions.loadTradeInContext(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [tradeInContext])

  const { children } = props

  return (
    <TradeInContextComponent
      intl={intl}
      loading={loading}
      campaign={campaign}
      animationOption={animationOptions}
    >
      {children}
    </TradeInContextComponent>
  )
}

TradeInContext.propTypes = {
  intl: PropTypes.shape().isRequired,
  actions: PropTypes.shape().isRequired,
  children: PropTypes.node.isRequired,
  tradeInContext: PropTypes.shape().isRequired,
  campaign: PropTypes.shape().isRequired,
  theme: PropTypes.shape().isRequired
}

const mapStateToProps = state => ({
  tradeInContext: state.configuration.tradeInContext,
  campaign: state.configuration.campaign,
  theme: state.configuration.theme
})

const mapDispatchToProps = (dispatch, ownProps) => ({
  actions: {
    loadTradeInContext: callback => {
      dispatch(async (__, getState) => {
        const { wizard, intl } = ownProps
        const { tradeIn, configuration } = getState()
        const { tradeInContext, campaign } = configuration
        const { tradeInConfig } = campaign
        const { car, customer } = tradeInContext

        const { estimatedDamages } = car
        const frontTyreDamage = estimatedDamages.find(x => x.type === 2 && x.frontTyreWear !== null)
        const rearTyreDamage = estimatedDamages.find(x => x.type === 2 && x.rearTyreWear !== null)

        let doReturn = false

        /**
         * Stop auto configuration process.
         * @param {*} sendNotification
         */
        const stopAutoConfiguration = async (sendNotification = true) => {
          await dispatch(
            setConfiguration({
              ...getState().configuration,
              tradeInContext: { loading: false }
            })
          )
          callback()
          if (sendNotification) {
            notification.info({
              message: intl.formatMessage({ id: 'info' }),
              description: intl.formatMessage({ id: 'trade-in-context-need-details' })
            })
          }
          doReturn = true
        }

        let user = null
        let furtherInformations = {
          ...tradeIn.futherInformations,
          funding: car.financing && car.financing.toString()
        }

        const { lang } = campaign

        if (customer) {
          const country = lang ? lang.split('-')[1] : null
          const parsedPhone = customer.cellphone
            ? parsePhoneNumber(customer.cellphone, country)
            : null
          user = {
            civility: customer.civility,
            lastName: customer.name,
            firstName: customer.firstname,
            email: customer.email,
            zipCode: customer.zipCode,
            cellphonePrefixCode: parsedPhone ? `+${parsedPhone.countryCallingCode}` : null,
            phoneNumberPrefixCode: parsedPhone ? `+${parsedPhone.countryCallingCode}` : null,
            phoneNumber: parsedPhone ? parsedPhone.nationalNumber : null,
            isCellphone: parsedPhone ? parsedPhone.getType() === 'MOBILE' : null,
            marketingConsent: customer.marketingConsent,
            thirdConsent: customer.thirdConsent
          }

          if (customer.purchaseNewVehicle !== null) {
            furtherInformations = {
              ...furtherInformations,
              purchase: {
                key: intl.formatMessage({
                  id: 'tradeIn-futher-informations-question-2-label'
                }),
                value: intl.formatMessage({
                  id: `tradeIn-futher-informations-question-2-reply-${customer.purchaseNewVehicle}`
                })
              }
            }
          }
        }

        let loadedTradeIn = {
          ...tradeIn,
          tradeInprice: null,
          vehicle: {
            catalog: car.catalog,
            tiresStateFront: frontTyreDamage ? frontTyreDamage.frontTyreWear : undefined,
            tiresStateBack: rearTyreDamage ? rearTyreDamage.rearTyreWear : undefined,
            pictures: car.pictures,
            mileage: car.mileage && car.mileage,
            dateOfRegistration: car.putIntoService,
            historical: {
              ...tradeIn.vehicle.historical,
              firstHand: car.firstHand ? 1 : 0,
              imported: car.import ? 1 : 0,
              maintained: car.upToDateMaintenanceBook ? 1 : 0
            },
            colors: {}
          },
          futherInformations: furtherInformations,
          user
        }

        await dispatch(setTradeIn(loadedTradeIn))

        /**
         * Process the distribution step.
         * @param {*} contextPointOfSale
         * @returns
         */
        const processDistributorStep = async contextPointOfSale => {
          const { id, zipCode } = contextPointOfSale

          let userCoordinates = { longitude: null, latitude: null }
          await RequesterGlobal.get(requesterGlobalMethods.GET_USER_GEOLOCATION, {
            zipCode,
            country: lang.split('-')[1]
          })
            .then(datas => {
              if (datas.longitude && datas.latitude) {
                userCoordinates = {
                  longitude: datas.longitude,
                  latitude: datas.latitude
                }
              }
            })
            .catch(() => stopAutoConfiguration())

          if (doReturn) return

          const { groupCampaign } = campaign
          let pointOfSale = null
          await RequesterGlobal.get(requesterGlobalMethods.GET_POINTS_OF_SALES, {
            groupCampaign,
            zipCode
          })
            .then(async datas => {
              const { items } = datas
              pointOfSale = items.find(x => id === x.wafyId)
            })
            .catch(() => stopAutoConfiguration())

          if (doReturn) return

          if (pointOfSale) {
            loadedTradeIn = {
              ...loadedTradeIn,
              pointOfSales: {
                ...pointOfSale,
                distance:
                  userCoordinates.longitude && userCoordinates.latitude
                    ? getDistanceFromCoordinates(userCoordinates, pointOfSale, lang.split('-')[1])
                    : null
              }
            }

            await dispatch(setTradeIn(loadedTradeIn))

            await RequesterConfiguration.get(requesterConfigurationMethods.GET_TOKEN, {
              token: campaign.uid,
              pointOfSaleId: pointOfSale.id
            })
              .then(async tokenResponse => {
                localStorage.setItem(
                  'bearer',
                  JSON.stringify({
                    jwt: tokenResponse.token
                  })
                )
              })
              .catch(() => stopAutoConfiguration())

            if (doReturn) return

            await RequesterConfiguration.get(requesterConfigurationMethods.GET_PRICING_POLICY)
              .then(async pricingPolicyResponse => {
                const { pricingPolicy } = pricingPolicyResponse
                await RequesterConfiguration.get(
                  requesterConfigurationMethods.GET_AUTOMATIC_VALIDATION_SETTINGS,
                  {
                    id: pricingPolicy.id,
                    automaticValidationId: pricingPolicy.automaticValidationId
                  }
                )
                  .then(async automaticValidationResponse => {
                    const { automaticValidation } = automaticValidationResponse

                    if (
                      moment(loadedTradeIn.vehicle.catalog).year() <
                      new Date().getFullYear() - automaticValidation.maximumAgeTradeInCars
                    ) {
                      stopAutoConfiguration(false)
                    }
                  })
                  .catch(() => stopAutoConfiguration())
              })
              .catch(() => stopAutoConfiguration())
          } else {
            stopAutoConfiguration()
          }
        }

        if (doReturn) return

        // Distributor step (config = 1)
        if (tradeInConfig === 1) {
          await dispatch(activateStep(getState().wizards, wizardStep.TRADEIN_DISTRIBUTOR))
          await processDistributorStep(tradeInContext.pointOfSale)
          if (doReturn) return
          wizard.next()
        }

        // Vehicle step
        const { referentialCarId, catalog } = car
        await RequesterTradeIn.get(RequesterTradeInMethods.GET_CAR_INFORMATIONS, {
          id: referentialCarId,
          catalog
        })
          .then(async datas => {
            const { vehicleInformations } = datas
            const { putIntoService, numberplate, chassis } = car

            const newVehicle = {
              ...loadedTradeIn.vehicle,
              ...vehicleInformations,
              id: referentialCarId,
              configuration: 3,
              putIntoService,
              numberplate: numberplate && numberplate.toUpperCase(),
              chassis,
              registration: putIntoService,
              catalog: vehicleInformations.modelCatalogBegin
            }

            loadedTradeIn = {
              ...loadedTradeIn,
              vehicle: {
                ...Hydrator.hydrate(
                  hydratorMethods.HYDRATE_VEHICLE,
                  newVehicle,
                  hydratorOrigins.ORIGIN_DETAIL
                ),
                price: 0,
                prices: {
                  totalOptionsPrice: 0,
                  totalServiceContractsPrice: 0,
                  totalPaintPrice: 0,
                  totalSaddleries: 0
                }
              }
            }

            await dispatch(setTradeIn(loadedTradeIn))

            await dispatch(activateStep(getState().wizards, wizardStep.TRADEIN_VEHICLE))
            await dispatch(activateStep(getState().wizards, wizardStep.TRADEIN_VEHICLE_CONFIRM))
            wizard.next()
          })
          .catch(() => {
            stopAutoConfiguration()
          })

        if (doReturn) return

        // Colors step
        let exteriorColor = null
        let interiorColor = null
        let serialPaint = null
        let serialSaddleries = null

        const { id } = getState().tradeIn.vehicle
        await RequesterTradeIn.get(RequesterTradeInMethods.GET_DESIGN, {
          id,
          catalog
        })
          .then(async saddleries => {
            const { leathers, fabrics, paints } = saddleries

            serialSaddleries = [
              ...leathers.filter(item => item.serial).map(x => ({ ...x, label: x.equipment })),
              ...fabrics.filter(item => item.serial).map(x => ({ ...x, label: x.equipment }))
            ]

            if (serialSaddleries.length === 0) {
              serialSaddleries = [
                { id: 'default', label: intl.formatMessage({ id: 'standard_upholstery' }) }
              ]
            }

            if (paints.filter(x => x.serial).length > 0) {
              serialPaint = _.first(paints.filter(item => item.serial))
            } else {
              serialPaint = { equipment: intl.formatMessage({ id: 'standard_painting' }) }
            }
          })
          .catch(() => {
            stopAutoConfiguration()
          })

        if (doReturn) return

        const { makeId, modelId } = getState().tradeIn.vehicle
        await RequesterTradeIn.get(RequesterTradeInMethods.GET_COLORS, {
          makeId,
          modelId
        })
          .then(async colorsResponse => {
            const { items } = colorsResponse
            const { internalColorReference, externalColorReference } = car

            if (internalColorReference) {
              interiorColor = _.first(
                items.filter(color =>
                  internalColorReference.toUpperCase().includes(color.wording.toUpperCase())
                )
              )
            }
            if (externalColorReference) {
              exteriorColor = _.first(
                items.filter(color =>
                  externalColorReference.toUpperCase().includes(color.wording.toUpperCase())
                )
              )
            }
          })
          .catch(() => {
            stopAutoConfiguration()
          })

        if (doReturn) return

        loadedTradeIn = {
          ...loadedTradeIn,
          vehicle: {
            ...loadedTradeIn.vehicle,
            interior: serialSaddleries,
            colors: {
              ...loadedTradeIn.vehicle.colors,
              type: serialPaint,
              exterior: exteriorColor,
              interior: interiorColor
            }
          }
        }

        await dispatch(setTradeIn(loadedTradeIn))

        if (
          loadedTradeIn.vehicle.interior &&
          loadedTradeIn.vehicle.colors.type &&
          loadedTradeIn.vehicle.colors.exterior &&
          loadedTradeIn.vehicle.colors.interior
        ) {
          await dispatch(activateStep(getState().wizards, wizardStep.TRADEIN_COLORS))
          wizard.next()
        } else {
          stopAutoConfiguration()
          return
        }

        // Options step
        await RequesterTradeIn.get(RequesterTradeInMethods.GET_EQUIPMENTS, {
          id,
          catalog
        })
          .then(async datas => {
            const { equipments, equipmentGroups } = datas
            const { idReferential } = campaign

            const serialOptions = equipments
              .filter(equipment => equipment.serial)
              .map(item => ({
                ...item,
                carEquipmentGroupings: getEquipmentGroupings(equipmentGroups, item, idReferential)
              }))
            const customOptions = car.equipments
              ? equipments
                  .filter(item => car.equipments.includes(Number(item.id)))
                  .map(item => ({
                    ...item,
                    carEquipmentGroupings: getEquipmentGroupings(
                      equipmentGroups,
                      item,
                      idReferential
                    )
                  }))
              : []

            loadedTradeIn = {
              ...loadedTradeIn,
              vehicle: {
                ...loadedTradeIn.vehicle,
                options: [...serialOptions, ...customOptions]
              }
            }

            await dispatch(setTradeIn(loadedTradeIn))

            await dispatch(activateStep(getState().wizards, wizardStep.TRADEIN_OPTIONS))
            wizard.next()
          })
          .catch(() => {
            stopAutoConfiguration()
          })

        if (doReturn) return

        // Historical step
        await dispatch(activateStep(getState().wizards, wizardStep.TRADEIN_HISTORICAL))
        wizard.next()
        if (
          loadedTradeIn.vehicle.mileage === undefined ||
          !loadedTradeIn.vehicle.dateOfRegistration ||
          !loadedTradeIn.futherInformations.funding
        ) {
          // Stop auto configuration if all needed data not filled
          stopAutoConfiguration()
          return
        }

        // State step
        if (
          loadedTradeIn.vehicle.tiresStateFront !== undefined &&
          loadedTradeIn.vehicle.tiresStateBack !== undefined
        ) {
          await dispatch(activateStep(getState().wizards, wizardStep.TRADEIN_STATE))
          wizard.next()
        } else {
          // Stop auto configuration if front & rear tires state not filled
          stopAutoConfiguration()
          return
        }

        // Defaults step
        await RequesterTradeIn.get(RequesterTradeInMethods.GET_VATS, {})
          .then(async vatsResponse => {
            const vat = vatsResponse.items.find(x => x.main)
            loadedTradeIn = {
              ...loadedTradeIn,
              vehicle: {
                ...loadedTradeIn.vehicle,
                tax: vat.rate,
                damages: estimatedDamages
                  .filter(damage => damage.type === 4)
                  .map(damage => ({ ...damage, sectionId: null })),
                defectsMustGoToManual: true
              }
            }

            await dispatch(setTradeIn(loadedTradeIn))
          })
          .catch(() => stopAutoConfiguration())

        await dispatch(activateStep(getState().wizards, wizardStep.TRADEIN_DEFAULTS))
        wizard.next()

        if (doReturn) return

        // Distributor step (config = 2)
        if (tradeInConfig === 2) {
          await dispatch(activateStep(getState().wizards, wizardStep.TRADEIN_DISTRIBUTOR))
          await processDistributorStep(tradeInContext.pointOfSale)
          if (doReturn) return
          wizard.next()
        }

        // Contact step
        await dispatch(activateStep(getState().wizards, wizardStep.TRADEIN_CONTACT))
        wizard.next()
        if (
          !(
            user.civility &&
            user.lastName &&
            user.firstName &&
            user.email &&
            user.zipCode &&
            user.phoneNumber &&
            furtherInformations.purchase
          )
        ) {
          stopAutoConfiguration()
          return
        }

        // Estimate step
        await dispatch(activateStep(getState().wizards, wizardStep.TRADEIN_ESTIMATE))
        wizard.next()
        stopAutoConfiguration(false)
      })
    }
  }
})

/* eslint-disable-next-line react-redux/prefer-separate-component-file */
const withConnect = connect(mapStateToProps, mapDispatchToProps)(TradeInContext)
const withIntl = injectIntl(withConnect)

export default withWizard(withIntl)
